import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { DisconnectOutlined } from "@/config/antd";
import WebIcon from "@/components/common/Web_Icons";
import { useSelector } from "react-redux";
import BaseInput from "@/components/common/inputs/Base_Input";
import {
  getInputBorderClass,
  getInputBackgroundClass,
  getIconColorClass,
  getFocusClasses,
  getTransitionClasses,
  isInputActive,
} from "@/helper/input_styles";

const LocationInput = ({
  label,
  placeholder = "Enter location",
  value,
  onChange,
  className = "",
  icon = <WebIcon icon="Location" size="small" />,
  disabled = false,
  id,
  ...props
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [predictions, setPredictions] = useState([]);
  const [showPredictions, setShowPredictions] = useState(false);
  const autocompleteService = useRef(null);
  const sessionToken = useRef(null);
  const isOnline = useSelector((state) => state.network.isOnline);

  // Check if Google Maps is already loaded (by App.jsx LoadScript)
  const isLoaded =
    isOnline && typeof window !== "undefined" && !!window.google?.maps?.places;

  useEffect(() => {
    if (isLoaded && !autocompleteService.current) {
      try {
        autocompleteService.current =
          new window.google.maps.places.AutocompleteService();
        sessionToken.current =
          new window.google.maps.places.AutocompleteSessionToken();
      } catch (error) {
        console.error("Error initializing Google Places service:", error);
      }
    }
  }, [isLoaded]);

  // Use shared styling utilities
  const isActive = isInputActive(value, isFocused);
  const iconColor = getIconColorClass(isActive);

  // Debounced search function
  const handleInputChange = async (e) => {
    const inputValue = e.target.value;
    onChange({ target: { value: inputValue } });

    if (inputValue.length > 2 && isOnline && autocompleteService.current) {
      try {
        const results = await autocompleteService.current.getPlacePredictions({
          input: inputValue,
          componentRestrictions: { country: "ng" },
          sessionToken: sessionToken.current,
        });

        setPredictions(results.predictions || []);
        setShowPredictions(true);
      } catch (error) {
        console.error("Error fetching predictions:", error);
        setPredictions([]);
        setShowPredictions(false);
      }
    } else {
      setPredictions([]);
      setShowPredictions(false);
    }
  };

  const handlePredictionClick = (prediction) => {
    onChange({ target: { value: prediction.description } });
    setPredictions([]);
    setShowPredictions(false);
  };

  // Offline mode UI
  if (!isOnline) {
    return (
      <BaseInput label={label} isActive={false}>
        <div className="absolute inset-y-0 left-3 flex items-center">
          <span className="pr-3 border-r border-gray-300 text-yellow-600">
            <DisconnectOutlined />
          </span>
        </div>
        <input
          type="text"
          className={`w-full py-6 pl-12 pr-10 
            bg-secondary rounded-md leading-5 text-gray-900
            border border-gray-300
            transition duration-150 ease-in-out ${className}`}
          placeholder="Location search unavailable offline"
          value={value}
          onChange={(e) => onChange({ target: { value: e.target.value } })}
          id={id}
          data-testid="location-input-field"
          {...props}
        />
        {value && (
          <div className="absolute bottom-2 right-2 text-xs text-yellow-600">
            Address validation unavailable
          </div>
        )}
      </BaseInput>
    );
  }

  // Loading UI (when online but Maps not yet loaded)
  if (!isLoaded) {
    return (
      <BaseInput label={label} isActive={false}>
        <input
          type="text"
          className={`w-full py-6 ${icon ? "pl-12" : "pl-3"} pr-10 
            bg-secondary rounded-md leading-5 text-gray-400
            border border-gray-300
            transition duration-150 ease-in-out ${className}`}
          placeholder="Loading Google Maps..."
          disabled
        />
      </BaseInput>
    );
  }

  // Normal online UI
  return (
    <BaseInput label={label} isActive={isActive}>
      <div className="absolute inset-y-0 left-3 flex items-center">
        {icon && (
          <span
            className="pr-3 border-r border-gray-300"
            data-testid="input-icon">
            {React.isValidElement(icon)
              ? React.cloneElement(icon, { className: iconColor })
              : icon}
          </span>
        )}
      </div>

      <input
        type="text"
        className={`w-full py-6 ${icon ? "pl-12" : "pl-3"} pr-10 
          ${getInputBackgroundClass(isActive)}
          rounded-md leading-5 text-black placeholder-gray-400 
          ${getInputBorderClass(isActive)}
          ${getFocusClasses()}
          ${getTransitionClasses()} ${className}`}
        placeholder={placeholder}
        value={value}
        onChange={handleInputChange}
        onFocus={() => setIsFocused(true)}
        onBlur={() => {
          setIsFocused(false);
          // Delay hiding predictions to allow for clicks
          setTimeout(() => setShowPredictions(false), 200);
        }}
        disabled={disabled}
        id={id}
        data-testid="location-input-field"
        {...props}
      />

      {/* Predictions Dropdown */}
      {showPredictions && predictions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 max-h-60 overflow-y-auto">
          {predictions.map((prediction) => (
            <div
              key={prediction.place_id}
              className="px-4 py-2 hover:bg-gray-100 cursor-pointer flex items-center gap-2"
              onClick={() => handlePredictionClick(prediction)}>
              <WebIcon icon="Location" size="small" />
              <div>
                <div className="text-sm font-medium">
                  {prediction.structured_formatting?.main_text}
                </div>
                <div className="text-xs text-gray-500">
                  {prediction.structured_formatting?.secondary_text}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </BaseInput>
  );
};

LocationInput.propTypes = {
  label: PropTypes.string,
  placeholder: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
  className: PropTypes.string,
  icon: PropTypes.element,
  disabled: PropTypes.bool,
  id: PropTypes.string,
};

export default LocationInput;
